import type { MouseEvent } from 'react';

import { AddCircle, ArrowForward, Article, CategoryOutlined, Edit, HexagonOutlined, MoreVert } from '@mui/icons-material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Chip,
	IconButton,
	Stack,
	type SxProps,
	type Theme,
	Tooltip,
	Typography,
	alpha,
} from '@mui/material';

import { EntityCard, EntityCardDetails, EntityCardPlaceholder } from '@@components';

import type { ContextModel, SubDomainModel } from '../../../api/api';
import { PresenceIndicator } from '../../../components/Presence/PresenceIndicator';
import { usePresence } from '../../../components/Presence/usePresence';
import type { ContextArtifactCounts } from '../Utils/contextArtifactCounts';

const contextChipSx: SxProps<Theme> = {
	color: 'primary.main',
	borderColor: (theme) => alpha(theme.palette.primary.main, 0.55),
	backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
	fontWeight: 600,
};

type ContextCardProps = {
	artifactCounts: ContextArtifactCounts;
	context: ContextModel;
	onEditDescription: (context: ContextModel) => void;
	onOpenResources: (context: ContextModel) => void;
	onOpenMenu: (event: MouseEvent<HTMLElement>, context: ContextModel) => void;
	onOpen: (context: ContextModel) => void;
};

function ContextCard({ artifactCounts, context, onEditDescription, onOpenResources, onOpenMenu, onOpen }: ContextCardProps) {
	const viewers = usePresence('context', context.id, 'definition', null, true);
	return (
		<EntityCard
			sx={{
				alignItems: 'stretch',
				borderRadius: 1,
				px: 2,
				pt: 2,
				pb: 1,
				minHeight: { xs: 210, sm: 232 },
				backgroundColor: (theme) => (theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.06) : alpha(theme.palette.primary.main, 0.025)),
				'&:hover': {
					backgroundColor: (theme) => (theme.palette.mode === 'dark' ? alpha(theme.palette.common.white, 0.09) : alpha(theme.palette.primary.main, 0.04)),
				},
			}}
		>
			<Stack direction="row" spacing={1} alignItems="flex-start">
				<HexagonOutlined color="primary" sx={{ fontSize: 26, mt: 0.125, flexShrink: 0 }} />
				<Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0, flexGrow: 1 }}>
					<Typography
						variant="subtitle1"
						color="text.primary"
						fontWeight={500}
						noWrap
						component="button"
						onClick={() => onOpen(context)}
						sx={{ border: 0, background: 'transparent', cursor: 'pointer', p: 0, textAlign: 'left', minWidth: 0 }}
					>
						{context.contextName}
					</Typography>
					<PresenceIndicator viewers={viewers} />
				</Stack>
				<Tooltip title="Context options">
					<IconButton
						size="small"
						onClick={(event) => onOpenMenu(event, context)}
						aria-label={`Options for ${context.contextName}`}
						sx={{ mt: -0.5, mr: -0.75 }}
					>
						<MoreVert fontSize="small" />
					</IconButton>
				</Tooltip>
			</Stack>
			<Stack direction="row" spacing={0.5} alignItems="flex-start" sx={{ mt: 1, minWidth: 0, flexGrow: 1 }}>
				<Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 3, overflow: 'hidden' }}>
					{context.description || 'No description'}
				</Typography>
				<Tooltip title="Edit context description">
					<IconButton size="small" onClick={() => onEditDescription(context)} sx={{ flexShrink: 0, p: 0.25 }}>
						<Edit sx={{ fontSize: 14 }} />
					</IconButton>
				</Tooltip>
			</Stack>
			<EntityCardDetails
				statistics={[
					{ label: 'Entities', value: artifactCounts.entities },
					{ label: 'Operations', value: artifactCounts.operations },
					{ label: 'Scopes', value: context.scopes?.length ?? 0 },
					{ label: 'Connections', value: context.serviceConnections?.length ?? 0 },
				]}
				action={
					<Stack direction="row" alignItems="center">
						<Button size="small" endIcon={<ArrowForward />} onClick={() => onOpen(context)} sx={{ justifyContent: 'flex-start', flexGrow: 1 }}>
							Open Context
						</Button>
						<Tooltip title="Context resources">
							<IconButton size="small" onClick={() => onOpenResources(context)}>
								<Article fontSize="small" />
							</IconButton>
						</Tooltip>
					</Stack>
				}
			/>
		</EntityCard>
	);
}

function CreateContextCard({ onCreate }: { onCreate: () => void }) {
	return (
		<EntityCardPlaceholder
			icon={<AddCircle color="primary" sx={{ fontSize: 34 }} />}
			title={
				<Typography variant="subtitle1" fontWeight={600}>
					Create a context
				</Typography>
			}
			description={
				<Typography variant="body2" color="text.secondary">
					Add a bounded context to this subdomain.
				</Typography>
			}
			action={
				<Button
					size="small"
					variant="contained"
					startIcon={<AddCircle />}
					onClick={(event) => {
						event.stopPropagation();
						onCreate();
					}}
				>
					Create Context
				</Button>
			}
			onClick={onCreate}
			sx={{ minHeight: { xs: 210, sm: 232 } }}
		/>
	);
}

type SubDomainSectionProps = {
	contextArtifactCountsById: Map<string, ContextArtifactCounts>;
	contexts: ContextModel[];
	isExpanded: boolean;
	onCreateContext: (subDomain: SubDomainModel) => void;
	onEditContextDescription: (context: ContextModel) => void;
	onEditSubDomainDescription: (subDomain: SubDomainModel) => void;
	onOpenContext: (context: ContextModel) => void;
	onOpenContextResources: (context: ContextModel) => void;
	onOpenContextMenu: (event: MouseEvent<HTMLElement>, context: ContextModel) => void;
	onOpenSubDomainResources: (subDomain: SubDomainModel) => void;
	onOpenMenu: (event: MouseEvent<HTMLElement>, subDomain: SubDomainModel) => void;
	onToggle: (subDomainId: string) => void;
	subDomain: SubDomainModel;
};

/** Expandable service subdomain, its context cards, and its creation placeholder. */
export default function SubDomainSection({
	contextArtifactCountsById,
	contexts,
	isExpanded,
	onCreateContext,
	onEditContextDescription,
	onEditSubDomainDescription,
	onOpenContext,
	onOpenContextResources,
	onOpenContextMenu,
	onOpenSubDomainResources,
	onOpenMenu,
	onToggle,
	subDomain,
}: SubDomainSectionProps) {
	return (
		<Accordion
			expanded={isExpanded}
			onChange={() => onToggle(subDomain.id)}
			disableGutters
			sx={{
				border: (theme) => `1px solid ${theme.palette.divider}`,
				borderRadius: 2,
				backgroundColor: 'background.paper',
				borderShadow: 0,
				boxShadow: 'none',
				'&:before': { display: 'none' },
			}}
		>
			<AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ '& .MuiAccordionSummary-content': { minWidth: 0, overflow: 'hidden' } }}>
				<Stack
					direction={{ xs: 'column', sm: 'row' }}
					spacing={{ xs: 1, sm: 2 }}
					alignItems={{ xs: 'stretch', sm: 'center' }}
					sx={{ width: '100%', minWidth: 0, pr: 1 }}
				>
					<Box sx={{ minWidth: 0, flex: '1 1 0', overflow: 'hidden' }}>
						<Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
							<CategoryOutlined fontSize="small" sx={{ flexShrink: 0, color: 'primary.main' }} />
							<Typography variant="subtitle1" color="text.primary" fontWeight={500} noWrap>
								{subDomain.name}
							</Typography>
						</Stack>
						<Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
							<Typography
								variant="body2"
								color="text.secondary"
								noWrap={!isExpanded}
								title={subDomain.description || 'No description'}
								sx={{
									minWidth: 0,
									flex: 1,
									overflow: 'hidden',
									textOverflow: isExpanded ? 'clip' : 'ellipsis',
									whiteSpace: isExpanded ? 'normal' : 'nowrap',
									overflowWrap: 'anywhere',
								}}
							>
								{subDomain.description || 'No description'}
							</Typography>
							<Tooltip title="Edit SubDomain description">
								<IconButton
									component="span"
									size="small"
									onClick={(event) => {
										event.stopPropagation();
										onEditSubDomainDescription(subDomain);
									}}
									sx={{ flexShrink: 0, p: 0.25 }}
								>
									<Edit sx={{ fontSize: 14 }} />
								</IconButton>
							</Tooltip>
						</Stack>
					</Box>
					<Box sx={{ flexShrink: 0, width: { xs: 'auto', sm: 160 }, display: 'flex', justifyContent: { xs: 'flex-start', sm: 'center' } }}>
						<Chip label={`${contexts.length} contexts`} size="small" variant="outlined" sx={contextChipSx} />
					</Box>
					<Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-start" sx={{ flexShrink: 0 }}>
						<Tooltip title="SubDomain resources">
							<IconButton
								component="span"
								size="small"
								onClick={(event) => {
									event.stopPropagation();
									onOpenSubDomainResources(subDomain);
								}}
								aria-label={`Open resources for ${subDomain.name}`}
							>
								<Article fontSize="small" />
							</IconButton>
						</Tooltip>
						<Tooltip title="SubDomain options">
							<IconButton component="span" size="small" onClick={(event) => onOpenMenu(event, subDomain)}>
								<MoreVert fontSize="small" />
							</IconButton>
						</Tooltip>
					</Stack>
				</Stack>
			</AccordionSummary>
			<AccordionDetails sx={{ pt: 0, pb: 2 }}>
				<Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fill, minmax(280px, 340px))' }, gap: 1.5 }}>
					{contexts.map((context) => (
						<ContextCard
							key={context.id}
							artifactCounts={contextArtifactCountsById.get(context.id) ?? { entities: 0, operations: 0 }}
							context={context}
							onEditDescription={onEditContextDescription}
							onOpenResources={onOpenContextResources}
							onOpenMenu={onOpenContextMenu}
							onOpen={onOpenContext}
						/>
					))}
					<CreateContextCard onCreate={() => onCreateContext(subDomain)} />
				</Box>
			</AccordionDetails>
		</Accordion>
	);
}
